import { withTranslation } from 'react-i18next';

import Loading from '../../../../components/Loading';
import MetricCards from '../../GscTools/components/MetricCards';
import PeriodTrend from '../../GscTools/components/PeriodTrend';
import { metricsFor, reportsPosition } from '../../GscTools/metrics';
import { formatPeriod } from '../../GscTools/period';
import { sourceLabel } from '../sources';

/**
 * Blocks 01 and 02 — what the selected source got, whether it has anything to report at all, and
 * how each day of the window went.
 *
 * One card, not two. The headline numbers and the daily lines are one reading of one scope — the
 * cards say where the window landed, the chart says how it got there — and picking a card
 * re-points the chart without fetching anything. A card heading between them would separate a
 * number from its own history and leave the cards controlling a chart in a different panel.
 *
 * Availability is the first question, not an afterthought. A property is not guaranteed to appear
 * in Discover, Google News or the News tab, and Search Console answering a source with nothing is
 * an ordinary outcome — so an empty response is stated as "no data returned for this source", not
 * drawn as zeroes, and it is kept apart from a request that failed. The first says something about
 * the property; the second says nothing about it at all.
 *
 * The cards are the window's own totals, from a request with no dimension. Which cards there are is
 * the source's business: Discover and Google News report no ranking position, so they get no
 * position card rather than an empty one. A day Search Console did not return stays a gap in the
 * line, never a zero — Discover in particular is spiky, and a line drawn through a missing day
 * would invent the smoothness it does not have.
 */
function SourceSummary({ t, scope, current, baseline, comparison, metric, onSelectMetric }) {
    const source = sourceLabel(t, scope.searchType);

    return (
        <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
            <div className="card-header d-flex justify-content-between">
                <div>
                    <p className="card-title">{t('tools.discover_news.summary.title')}</p>
                    <p className="gsc-tool-sub">
                        {scope.baseline
                            ? t('tools.discover_news.summary.compared', {
                                  source,
                                  current: formatPeriod(scope.current),
                                  baseline: formatPeriod(scope.baseline),
                              })
                            : `${source} · ${formatPeriod(scope.current)}`}
                    </p>
                </div>
            </div>

            <div className="card-body">
                {(current?.status === 'idle' || current?.status === 'loading') && (
                    <Loading padding={20} />
                )}

                {current?.status === 'error' && (
                    <div className="gsc-tool-banner error">
                        {t('tools.discover_news.summary.failed', {
                            source,
                            reason: current.error ?? t('global.something_went_wrong'),
                        })}
                    </div>
                )}

                {current?.status === 'done' && !current.totals && (
                    <div className="gsc-tool-load-state">
                        <span className="gsc-tool-load-icon" aria-hidden="true">
                            <i className="icon-compass-03" />
                        </span>
                        <h4>{t('tools.discover_news.summary.unavailable_title', { source })}</h4>
                        <p>{t('tools.discover_news.summary.unavailable', { source })}</p>
                    </div>
                )}

                {current?.status === 'done' && !!current.totals && (
                    <>
                        <MetricCards
                            metrics={metricsFor(scope.searchType)}
                            totals={current.totals}
                            priorTotals={baseline?.totals}
                            comparison={comparison}
                            selectedMetric={metric}
                            onSelectMetric={onSelectMetric}
                        />
                        <p className="gsc-tool-hint">
                            {t('tools.discover_news.summary.pick_card')}
                        </p>

                        {/* Above the chart rather than instead of it: the baseline failing takes
                            the comparison line away, not the current window. */}
                        {baseline?.status === 'error' && (
                            <div className="gsc-tool-banner error">
                                {t('tools.discover_news.summary.baseline_failed', {
                                    reason: baseline.error ?? t('global.something_went_wrong'),
                                })}
                            </div>
                        )}

                        <PeriodTrend
                            metric={metric}
                            current={current}
                            baseline={
                                scope.baseline && baseline?.status === 'done' ? baseline : null
                            }
                            fileName={`${t('tools.gsc.trend.title')} — ${source}`}
                        />

                        <p className="gsc-tool-table-note">
                            {t('tools.discover_news.summary.no_inference', { source })}
                        </p>
                    </>
                )}

                <p className="gsc-tool-table-note">
                    {reportsPosition(scope.searchType)
                        ? t('tools.discover_news.summary.position_reported', { source })
                        : t('tools.discover_news.summary.no_position', { source })}
                </p>
            </div>
        </div>
    );
}

export default withTranslation()(SourceSummary);
